import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteVehicleFuelType, useFilterVehicleFuelTypes } from "../../../../../common/api/queries/vehicleFuelType";
import ActionTextIcon from "../../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../../common/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "../../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../../common/constants";
import { CertificateFuelType, CertificateFuelTypeFilterPageRequest } from "../../types";

interface Props {
  searchParams: CertificateFuelTypeFilterPageRequest;
  onUpdateClick: (certificateVehicleFuelType: CertificateFuelType) => void;
}

export const VehicleFuelTypeTable = ({ searchParams, onUpdateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isFetching } = useFilterVehicleFuelTypes(searchParams);
  const deleteVehicleFuelType = useDeleteVehicleFuelType();

  const columns: ColumnsType<CertificateFuelType> = [
    {
      key: "fuelType",
      title: t("contract.enums.fuelType._label"),
      width: 150,
      ellipsis: { showTitle: false },
      render: (_, record) => record.fuelType && <Ellipsis>{t("contract.enums.fuelType." + record.fuelType)}</Ellipsis>,
    },
    {
      key: "name",
      title: t("vehicleFuelType.attrs.name"),
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
    },
    {
      key: "source",
      title: t("vehicle.enums.fuelNameSource._label"),
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{t("vehicle.enums.fuelNameSource." + record.source)}</Ellipsis>,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("vehicleFuelType.helpers.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={() => deleteVehicleFuelType.mutate(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Card className="card-box">
      <Table<CertificateFuelType>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.SMALL }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />
    </Card>
  );
};
